import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Menu, X, Sparkles, LogOut, ShieldCheck, Phone, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import logoAsset from "@/assets/logo.png.asset.json";

const NAV = [
  { to: "/studio", label: "AI Paint Studio" },
  { to: "/estimate", label: "Instant Estimate" },
  { to: "/gallery", label: "Gallery" },
  { to: "/services", label: "Services" },
  { to: "/contact", label: "Contact" },
] as const;

export function Header() {
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setUserId(data.session?.user.id ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setUserId(session?.user.id ?? null);
    });
    return () => { active = false; sub.subscription.unsubscribe(); };
  }, []);

  useEffect(() => {
    if (!userId) { setIsAdmin(false); return; }
    let active = true;
    supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle()
      .then(({ data }) => { if (active) setIsAdmin(!!data); });
    return () => { active = false; };
  }, [userId]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <>
    <header className="fixed top-0 left-0 right-0 z-50 w-full bg-[#030303]/75 backdrop-blur-xl border-b border-white/5 lg:sticky">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 sm:h-20 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 lg:flex lg:justify-between">
        <div className="flex min-w-0 items-center gap-10">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src={logoAsset.url} alt="Michael's Painting" width={514} height={112} className="h-8 sm:h-11 w-auto" />
          </Link>
          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="text-sm font-medium text-zinc-200 hover:text-white transition-colors inline-flex items-center gap-1.5"
                activeProps={{ className: "text-white" }}
              >
                {n.to === "/studio" && <Sparkles className="size-3.5 text-amber-300" />}
                {n.label}
              </Link>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {isAdmin && (
            <Link
              to="/admin/estimates"
              className="hidden md:inline-flex items-center gap-1.5 text-xs font-medium text-amber-200 hover:text-amber-100"
            >
              <ShieldCheck className="size-3.5" /> Admin
            </Link>
          )}
          {userId ? (
            <button
              onClick={signOut}
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-zinc-200 hover:text-white"
            >
              <LogOut className="size-4" /> Sign out
            </button>
          ) : (
            <Link
              to="/auth"
              className="hidden sm:inline-flex text-sm font-medium text-zinc-300 hover:text-white"
            >
              Sign in
            </Link>
          )}
          <Link
            to="/estimate"
            className="hidden sm:inline-flex bg-white text-zinc-950 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold hover:bg-amber-300 transition-all duration-300 shadow-[0_0_30px_rgba(255,255,255,0.08)]"
          >
            Free Estimate
          </Link>
          <button
            className="lg:hidden text-white touch-target grid place-items-center -mr-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X className="size-6" /> : <Menu className="size-6" />}
          </button>
        </div>
      </div>
    </header>
    {/* Mobile slide-out drawer (rendered outside header to escape its backdrop-filter containing block) */}
    {open && (
        <div className="lg:hidden fixed top-14 right-0 bottom-0 left-0 z-40">
          <button
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <div className="absolute right-0 top-0 bottom-0 w-[86vw] max-w-sm bg-[#050505] border-l border-white/10 overflow-y-auto safe-bottom shadow-2xl">
            <nav className="flex flex-col px-5 py-4">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  className="py-4 text-zinc-100 text-base font-medium border-b border-white/5 inline-flex items-center gap-2 touch-target"
                >
                  {n.to === "/studio" && <Sparkles className="size-4 text-amber-300" />}
                  {n.label}
                </Link>
              ))}
              {isAdmin && (
                <Link to="/admin/estimates" className="py-4 text-amber-200 text-base font-medium border-b border-white/5 inline-flex items-center gap-2 touch-target">
                  <ShieldCheck className="size-4" /> Admin Dashboard
                </Link>
              )}
              {userId ? (
                <>
                  <Link to="/portal/dashboard" className="py-4 text-zinc-100 text-base font-medium border-b border-white/5 touch-target">My Portal</Link>
                  <button onClick={signOut} className="py-4 text-left text-zinc-300 text-base font-medium border-b border-white/5 inline-flex items-center gap-2 touch-target">
                    <LogOut className="size-4" /> Sign out
                  </button>
                </>
              ) : (
                <Link to="/auth" className="py-4 text-zinc-100 text-base font-medium border-b border-white/5 touch-target">Sign in</Link>
              )}
              <Link
                to="/estimate"
                className="mt-6 inline-flex items-center justify-center gap-2 bg-white text-zinc-950 px-5 py-4 rounded-full text-sm font-semibold touch-target"
              >
                Get Free Estimate <ArrowRight className="size-4" />
              </Link>
              <a
                href="tel:6088864598"
                className="mt-3 inline-flex items-center justify-center gap-2 border border-white/15 text-zinc-100 px-5 py-4 rounded-full text-sm font-medium touch-target"
              >
                <Phone className="size-4" /> Call (608) 886-4598
              </a>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}